import { auth } from '@/features/auth/types';
import { type GuardianProfile } from '@/features/guardian/services/get-guardian';
import {
  INITIAL_ONBOARDING_PROFILE_FORM,
  type OnboardingProfileForm,
} from '@/features/onboarding/schemas/onboarding-profile-schema';
import { countPhoneDigits } from '@/utils/helper';
import { AsYouType, isPossiblePhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

import { countryCodeToName, countryNameToCode } from './country-helpers';

/** Role option value (lowercase) → `GuardianRelationship` accepted by the API. */
const RELATIONSHIP_BY_ROLE: Record<string, auth.GuardianRelationship> = {
  parent: 'Parent',
  grandparent: 'Grandparent',
  guardian: 'Guardian',
  other: 'Other',
};

/** Inverse of {@link RELATIONSHIP_BY_ROLE}: API relationship → role option value. */
const ROLE_BY_RELATIONSHIP: Record<auth.GuardianRelationship, string> = {
  Parent: 'parent',
  Grandparent: 'grandparent',
  Guardian: 'guardian',
  Other: 'other',
};

/**
 * Formats a stored phone (digits-only or already `+`-prefixed) for the phone
 * input, and recovers the dial country for its flag. Mirrors the player form's
 * prefill so both screens render saved numbers the same way.
 */
function formatPhoneForInput(raw?: string): { value: string; country: string | null } {
  if (!raw) return { value: '', country: null };
  const normalized = raw.startsWith('+') ? raw : `+1${raw}`;
  const value = new AsYouType().input(normalized);
  const parsed = isPossiblePhoneNumber(normalized)
    ? parsePhoneNumberFromString(normalized)
    : undefined;
  return { value, country: parsed?.country ?? null };
}

/**
 * Builds the onboarding step 1 (profile) form values from the guardian's saved
 * profile (`GET /api/v1/guardians/me`), so the wizard prefills whatever already
 * exists. Missing fields fall back to {@link INITIAL_ONBOARDING_PROFILE_FORM};
 * `email` is server-owned and falls back to the signed-in user's email.
 */
export function mapGuardianProfileToOnboardingForm(
  guardian: GuardianProfile | undefined,
  fallbackEmail = '',
): OnboardingProfileForm {
  if (!guardian) {
    return {
      ...INITIAL_ONBOARDING_PROFILE_FORM,
      email: fallbackEmail || INITIAL_ONBOARDING_PROFILE_FORM.email,
    };
  }

  const phone = formatPhoneForInput(guardian.phone);
  const altPhone = formatPhoneForInput(guardian.altPhone);

  return {
    firstName: guardian.firstname ?? '',
    lastName: guardian.lastname ?? '',
    role: guardian.relationship
      ? (ROLE_BY_RELATIONSHIP[guardian.relationship as auth.GuardianRelationship] ??
        INITIAL_ONBOARDING_PROFILE_FORM.role)
      : INITIAL_ONBOARDING_PROFILE_FORM.role,
    phoneNumber: phone.value || INITIAL_ONBOARDING_PROFILE_FORM.phoneNumber,
    countryCode: phone.country,
    altPhoneNumber: altPhone.value,
    altCountryCode: altPhone.country,
    email: guardian.email ?? fallbackEmail ?? '',
    country: countryNameToCode(guardian.address?.country),
    city: guardian.address?.city ?? '',
    state: guardian.address?.state ?? null,
    zipCode: guardian.address?.zipcode ?? '',
    avatar: guardian.photoUrl ? { uri: guardian.photoUrl } : null,
  };
}

/**
 * Maps onboarding step 1 (profile) form values into the
 * `GuardianProfileRequestDto` body for PATCH `/api/v1/guardians/me`.
 *
 * `email` is server-owned (read-only in the form) so it is never sent. Optional
 * fields are omitted when empty — the alt phone only ships once it has a valid
 * (≥10-digit) number, and the address block is dropped entirely when blank.
 *
 * `photoUrl` (the uploaded avatar's URL) is included only when provided by the
 * caller after a successful upload.
 */
export function mapOnboardingProfileToGuardianRequest(
  values: OnboardingProfileForm,
  photoUrl?: string,
): auth.GuardianProfileUpdateRequest {
  const request: auth.GuardianProfileUpdateRequest = {
    firstname: values.firstName.trim(),
    lastname: values.lastName.trim(),
    relationship: RELATIONSHIP_BY_ROLE[values.role] ?? 'Parent',
    phone: values.phoneNumber.trim(),
  };

  if (photoUrl) {
    request.photoUrl = photoUrl;
  }

  if (countPhoneDigits(values.altPhoneNumber) >= 10) {
    request.altPhone = values.altPhoneNumber.trim();
  }

  const address: auth.Address = {};
  const city = values.city.trim();
  const zipcode = values.zipCode.trim();
  const country = countryCodeToName(values.country);
  if (country) address.country = country;
  if (city) address.city = city;
  if (values.state) address.state = values.state;
  if (zipcode) address.zipcode = zipcode;
  if (Object.keys(address).length > 0) {
    request.address = address;
  }

  return request;
}
